import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Ban, Flag, Gavel, ShieldAlert, ShieldCheck, ShieldX, Timer, UserCheck, UserX, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { WaitlistForm } from "./WaitlistForm";

const PILLARS = [
  {
    icon: ShieldCheck,
    slug: "verification",
    title: "Verified from day one",
    body: "Every member verifies their identity before they can match. The badge isn't a paid upgrade or a popularity contest — it's the price of entry. No verification, no access.",
  },
  {
    icon: UserCheck,
    slug: "no-anonymous",
    title: "No anonymous profiles",
    body: "Real names, real photos, real people. There's no lurking behind a blank avatar here — everyone you see has shown us exactly who they are.",
  },
  {
    icon: Users,
    slug: "reports",
    title: "Humans review every report",
    body: "Reports don't disappear into an algorithm. A person reads each one, and people who break the rules hear about it — with warnings, suspensions, or permanent bans.",
  },
  {
    icon: Timer,
    slug: "expiration",
    title: "The 120-hour rule",
    body: "Matches expire after 120 hours. Nobody can collect you, bench you, or keep you as an option. Every connection here is intentional — or it's gone.",
  },
  {
    icon: Ban,
    slug: "scams",
    title: "Zero tolerance for scams",
    body: "Asking for money, gift cards, or 'investments' means an immediate, permanent ban. Because every member is verified, banned actually means banned — they can't just make a new account.",
  },
  {
    icon: Flag,
    slug: "control",
    title: "One-tap block & report",
    body: "Block anyone instantly — they vanish from your world completely. Reporting is one tap, confidential, and retaliation against reporters is itself a violation.",
  },
];

const TRUST_POLICIES = [
  {
    icon: ShieldAlert,
    title: "Anti-Human Trafficking Policy",
    body: "Community Hearts has zero tolerance for human trafficking or sexual exploitation. Any content, profiles, or communications that suggest trafficking, coercion, or exploitation are immediately removed. We report suspected trafficking activity to the National Human Trafficking Hotline and relevant law enforcement. You can report suspected trafficking directly through the app's report feature.",
    tag: "National Human Trafficking Hotline · 1-888-373-7888",
  },
  {
    icon: Gavel,
    title: "Illegal Activity Prevention",
    body: "Community Hearts prohibits using the platform to facilitate any illegal activity, including solicitation, drug transactions, violence, or fraud. Automated moderation and user reports are reviewed by admins. Accounts found facilitating illegal activity are permanently banned and reported to law enforcement where required.",
    tag: null,
  },
  {
    icon: UserX,
    title: "Impersonation Prevention",
    body: "Impersonating another person, public figure, or entity is prohibited. Photo verification via live selfie helps confirm each member's identity. Reported impersonation accounts are immediately suspended pending review and permanently removed if confirmed.",
    tag: null,
  },
  {
    icon: ShieldX,
    title: "Child Sexual Abuse Material (CSAM) Policy",
    body: "Community Hearts has absolute zero tolerance for CSAM. Any detected or reported CSAM is immediately removed, the account is permanently banned, and the content is reported to the National Center for Missing & Exploited Children (NCMEC) CyberTipline as required by federal law. We cooperate fully with law enforcement in all CSAM investigations.",
    tag: "NCMEC CyberTipline · report.cybertip.org",
  },
];

const cardClass =
  "group h-full scroll-mt-28 rounded-3xl border border-[#C4A36A]/35 bg-[#FBF8F3] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#C4A36A]/70 target:border-[#C4A36A]";

const iconClass =
  "flex h-12 w-12 items-center justify-center rounded-full border border-[#C4A36A]/50 bg-[#C4A36A]/10 text-[#A08448] transition-colors duration-500 group-hover:bg-[#C0965A] group-hover:text-[#FFF8E8]";

const SafetyPage = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const t = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen overflow-x-clip bg-[#F7F3EA] font-sans text-[#1A120C]" data-testid="safety-page">
      <header className="relative z-[1] border-b border-[#C4A36A]/25 bg-[#F7F3EA]">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="safety-logo">
            <img src="/assets/hero-crest.png?v=2" alt="" className="h-10 w-auto" />
            <span className="font-serif text-sm font-medium uppercase tracking-[0.22em] text-[#A08448]">
              Community Hearts
            </span>
          </Link>
          <Link
            to="/"
            className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A08448] transition-colors hover:text-[#7A5A14]"
            data-testid="safety-back-link"
          >
            Back to the site
          </Link>
        </div>
      </header>

      <main className="relative z-[1]">
        <section className="mx-auto max-w-4xl px-6 pb-20 pt-20 text-center md:pt-28">
          <Reveal>
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]" data-testid="safety-overline">
              Safety at Community Hearts
            </p>
            <h1
              className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-[#1A120C] md:text-6xl"
              data-testid="safety-headline"
            >
              Safe isn't a feature here.
              <br />
              <span className="italic text-[#A08448]">It's the foundation.</span>
            </h1>
            <p className="mx-auto mt-8 max-w-2xl font-serif text-base leading-relaxed text-[#3A2C20] md:text-lg">
              Dating apps ask you to trust strangers. We built Community Hearts so you don't have to —
              every rule, every feature, every design choice starts with one question: does this keep
              our people safe?
            </p>
          </Reveal>
        </section>

        <section className="border-y border-[#C4A36A]/30 bg-[#F7F1E6]">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={0.05 + i * 0.07}>
                  <div id={p.slug} className={cardClass} data-testid={`safety-pillar-${i + 1}`}>
                    <div className={iconClass}>
                      <p.icon className="h-5 w-5" />
                    </div>
                    <h2 className="mt-6 font-serif text-2xl font-medium tracking-tight text-[#1A120C]">{p.title}</h2>
                    <p className="mt-3 font-serif text-sm leading-relaxed text-[#3A2C20]">{p.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <motion.p
                className="mx-auto mt-20 max-w-3xl text-center font-serif text-2xl italic leading-relaxed text-[#1A120C] md:text-4xl"
                data-testid="safety-promise"
              >
                "If someone's here to play games,{" "}
                <span className="text-[#A08448]">they're not here for long.</span>"
              </motion.p>
              <p className="mt-8 text-center font-serif text-sm text-[#5C4632]">
                Read the full rules in our{" "}
                <Link
                  to="/acceptable-use"
                  className="text-[#A08448] underline underline-offset-4 transition-colors hover:text-[#7A5A14]"
                  data-testid="safety-aup-link"
                >
                  Acceptable Use Policy
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </section>

        <section id="trust-safety" className="mx-auto max-w-5xl scroll-mt-28 px-6 py-20 md:py-28" data-testid="trust-safety-section">
          <Reveal>
            <div className="text-center">
              <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]" data-testid="trust-safety-overline">
                Trust &amp; Safety Policies
              </p>
              <h2
                className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-[#1A120C] md:text-5xl"
                data-testid="trust-safety-headline"
              >
                The lines we <span className="italic text-[#A08448]">will not cross.</span>
              </h2>
              <p className="mx-auto mt-6 max-w-2xl font-serif text-base leading-relaxed text-[#3A2C20]">
                Some things aren't up for debate. These are the policies that protect our community and the law we hold ourselves to.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid gap-5 md:grid-cols-2">
            {TRUST_POLICIES.map((p, i) => (
              <Reveal key={p.title} delay={0.05 + i * 0.07}>
                <div
                  className="group flex h-full flex-col rounded-3xl border border-[#C4A36A]/35 bg-[#FBF8F3] p-8 transition-all duration-500 hover:-translate-y-1 hover:border-[#C4A36A]/70"
                  data-testid={`trust-policy-${i + 1}`}
                >
                  <div className={iconClass}>
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-6 font-serif text-2xl font-medium tracking-tight text-[#1A120C]">{p.title}</h3>
                  <p className="mt-3 font-serif text-sm leading-relaxed text-[#3A2C20]">{p.body}</p>
                  {p.tag && (
                    <p
                      className="mt-6 rounded-xl border border-[#C4A36A]/30 bg-[#C4A36A]/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-[#A08448]"
                      data-testid={`trust-policy-${i + 1}-tag`}
                    >
                      {p.tag}
                    </p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-6 py-20 text-center md:py-28">
          <Reveal>
            <h2
              className="font-serif text-3xl font-medium tracking-tight text-[#1A120C] md:text-5xl"
              data-testid="safety-cta-headline"
            >
              The safest seat in dating
              <br />
              has <span className="italic text-[#A08448]">your name on it.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl font-serif text-base leading-relaxed text-[#3A2C20]">
              Join the waitlist and be there when your city unlocks — verified, protected, and done playing games.
            </p>
            <div className="mx-auto mt-10 max-w-xl">
              <WaitlistForm testIdPrefix="safety-waitlist" />
            </div>
          </Reveal>
        </section>
      </main>
    </div>
  );
};

export default SafetyPage;
